/**
 * 支付方式图标设置（App 级 per 支付方式）契约 schema —— dev-docs `02-API契约/A8-运营支付.md`
 * §7.8.5~§7.8.7（ADM-APP-S013 图标配置）。
 *
 * **图标按支付方式配、不按结算通道配**（`03-数据字典/70-产品配置.md` §3.7.3）：三个 `STRIPE_*` 各配
 * 各的，`payment_method` 取值域 = §3 两层闭集的 `PAYMENT_METHODS` 层，传结算通道值 `STRIPE` 即拒。
 *
 * **闭集全部取值都可配图标（含 `IAP`）**：dd#1051 起客户端不持有任何内置通道图标，C 端 `icon_url`
 * 恒非空下发；srv 0199 已解除「`IAP` / `STRIPE` 图标须为 NULL」的 DB 约束。与之配对的是「策略成员 ⇒
 * 有图标」双向闸（§7.8.2 进入 / §7.8.6 停留），两侧都对全闭集生效。
 */

import { z } from 'zod';
import { type PaymentMethod, PaymentMethodSchema } from './payment-methods';

/**
 * 图标资产标识：§7.8.7 上传返回的 UUID v7。
 * 用 `.uuid()` 而非 v7 专用校验，与全仓既有 id 入参口径一致（v7 是 UUID 的子集，
 * 「必须是 v7」由服务端生成侧保证，不由入参侧反推）。
 */
export const PaymentMethodIconAssetIdSchema = z.string().uuid();

/** GET /api/v1/payment-method-settings query（§7.8.5）：`app_id` 必填，缺失 / 非法 → VALIDATION_FAILED。 */
export const ListPaymentMethodSettingsQuerySchema = z.object({
  app_id: z.string().uuid(),
});
export type ListPaymentMethodSettingsQuery = z.infer<typeof ListPaymentMethodSettingsQuerySchema>;

/**
 * PUT /api/v1/payment-method-settings 请求体（§7.8.6）。
 * **strict**：请求体字段闭集 = 下面四键，任何额外字段 → VALIDATION_FAILED（typo 不静默 strip）。
 * `icon_asset_id` 必填但可为 `null`（= 清空图标）—— 故用 `.nullable()` 而非 `.nullish()`：省略该键即校验失败。
 * 「这次能不能置 `null`」是**跨表**事实（目标支付方式是否仍是某策略组成员），由 service 层在 App 级
 * 图标守卫锁内判并回 `details.payment_method`；Zod 只保证形状。
 */
export const UpsertPaymentMethodSettingRequestSchema = z
  .object({
    app_id: z.string().uuid(),
    payment_method: PaymentMethodSchema,
    icon_asset_id: PaymentMethodIconAssetIdSchema.nullable(),
    // 长度同时挡住 JS 正则 $ 可匹配末尾换行的情况。
    icon_background_color: z
      .string()
      .length(7)
      .regex(/^#[0-9A-F]{6}$/)
      .nullable()
      .optional(),
  })
  .strict();
export type UpsertPaymentMethodSettingRequest = z.infer<
  typeof UpsertPaymentMethodSettingRequestSchema
>;

/**
 * 单条支付方式设置（§7.8.5 item shape，§7.8.6 响应复用）。
 * `icon_url` 是**派生字段**（不落库）：由 `icon_asset_id` 拼 C 端 §1.2 公开静态路由的绝对 HTTPS URL；
 * `icon_asset_id` 为 null 时同为 null。
 */
export type PaymentMethodSettingResponse = {
  payment_method: PaymentMethod;
  icon_asset_id: string | null;
  icon_background_color: string | null;
  icon_url: string | null;
};

/** POST /api/v1/payment-method-icons 响应（§7.8.7）：新生成的资产标识 + 其绝对 URL（本端点不写 DB）。 */
export type UploadPaymentMethodIconResponse = {
  icon_asset_id: string;
  icon_url: string;
};
